import {apolloClient, gql} from '../graphql';
import {logAndThrowGraphQLErrors, normalizeGraphQLObject} from '../graphql/helpers';
import {formatIsoDate, parseIsoDate} from 'shared/src/datetime';
import type {ID, Invoice} from 'shared/src/models';

export const getInvoices = async ({customerId}: {customerId?: string} = {}): Promise<Invoice[]> => {
	const {data, error} = await apolloClient.query({
		query: gql(/* GraphQL */ `
			query GetInvoices {
				invoices {
					id
					publicId
					date
					description
					customer {
						id
					}
					items {
						id
						type
						name
						description
						quantity
						price
						taxed
					}
				}
			}
		`),
		fetchPolicy: 'no-cache',
	});

	if (error) {
		throw error;
	}

	let invoices = data.invoices;

	if (customerId) {
		invoices = invoices.filter((invoice) => {
			return invoice.customer.id === customerId;
		});
	}

	return invoices.map((invoice) => ({
		...normalizeGraphQLObject(invoice),
		date: parseIsoDate(invoice.date),
		customer: invoice.customer.id,
		items: invoice.items.map(normalizeGraphQLObject),
	}));
};

export const getInvoice = async (invoiceId: string): Promise<Invoice> => {
	const invoice = (await getInvoices()).find((invoice) => {
		return invoice.id === invoiceId;
	});

	if (!invoice) {
		throw new Error('Invoice not found');
	}

	return invoice;
};

type NewOrUpdatedInvoice = Omit<Invoice, 'id'> & Partial<Pick<Invoice, 'id'>>;

export const saveInvoice = async (invoice: NewOrUpdatedInvoice): Promise<ID> => {
	const {data, errors} = await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation SaveInvoice($invoice: InvoiceInput!) {
				saveInvoice(invoice: $invoice)
			}
		`),
		variables: {
			invoice: {
				id: invoice.id,
				date: formatIsoDate(invoice.date),
				description: invoice.description,
				customer: {
					id: invoice.customer,
				},
				items: invoice.items.map((item) => ({
					id: item.id,
					type: item.type,
					name: item.name,
					description: item.description,
					quantity: item.quantity,
					price: item.price,
					taxed: Boolean(item.taxed),
				})),
			},
		},
	});

	if (data?.saveInvoice) {
		return data.saveInvoice;
	} else {
		if (errors) {
			logAndThrowGraphQLErrors(errors);
		}

		throw new Error('Error while saving invoice');
	}
};
